import { memo } from 'react';
import type { VFC } from 'react';

import type { AssetListComponent } from '@vgtv/api-client/lib/component/list';

import { List } from '../../../List/List';
import { AssetHorizontal } from '../../../Asset/AssetHorizontal/AssetHorizontal';
import { useComponentDataFetch } from '../../useComponentDataFetch';
import { fetchSvpAssets } from '../../../../modules/assets';
import { getAssetMainImage } from '../../../../shared/helpers/assetHelpers';
import { getImageSources } from '../../../../helpers/images';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';

import * as styles from './HorizontalAssetsList.module.scss';

interface Props {
  componentConfig: AssetListComponent;
  pageComponentIndex: number;
}
const HorizontalAssetsListBase: VFC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isFetched } = useComponentDataFetch({
    componentConfig,
    fetchAction: fetchSvpAssets,
  });

  const { id, header } = componentConfig;

  const assets = useComponentAssets(id);

  const { trackAssetClick } = useAssetItemTracking({
    elementType: 'HorizontalAssetsList',
    header: componentConfig.header,
    pageComponentIndex,
    skin: 'horizontal',
  });

  if (isFetched && assets.length === 0) {
    return null;
  }

  return (
    <List header={header} className={styles.list}>
      {assets.map((asset, index) => {
        const { id, title, duration } = asset;
        const imageUrl = getAssetMainImage(asset);

        const imageSources = getImageSources({
          imageUrl,
          imagePreset: 'assetsListHorizontal',
        });

        return (
          <AssetHorizontal
            key={id}
            asset={asset}
            title={title}
            duration={duration}
            imageSources={imageSources}
            onClick={() => trackAssetClick(asset, index)}
          />
        );
      })}
    </List>
  );
};

export const HorizontalAssetsList = memo(HorizontalAssetsListBase);
